import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { mobileBreakpoint, useIsMobile, useL10n } from '@uplift/hooks';
import {
  Button, EditableSectionRow,
  ContentStack
} from '@uplift/ui-v2';

import { CardStatusInlineAlert } from '../alerts';
import { useBillingContext } from '../billing.context';
import { DesktopOnly, MobileOnly } from './view-port-render.component';
import { useUserBillingQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';

export type PaymentMethodViewProps = {
  onClick?: () => void;
};

export const PaymentMethodView: React.FC<PaymentMethodViewProps> = ({ onClick }) => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const { classes } = useStyles();
  const isMobile = useIsMobile(mobileBreakpoint);
  const { addressFormatted, cardEndingIn, cardStatus } = useBillingContext();
  const { data: billingData } = useUserBillingQuery(me.id, { suspense: true });
  const isBillingMissing = !billingData;

  return (
    <Box display="flex">
      <Box display="flex" flexDirection="column" flexGrow={1} >
        <EditableSectionRow
          className={classes.paddingBottom0}
          rowKey="cardDetails"
          label={l10n.accountSettings.billing.paymentCard}
          kind="custom"
          hideLabel={isMobile}
          hideRowDisplay={isBillingMissing}
          hideEditButton
        >
          <MobileOnly>
            <ContentStack title={l10n.accountSettings.billing.paymentCard} value={cardEndingIn}>
              <Box mt={1} />
              <Box display="flex" alignItems="center">
                <Typography component="span" variant="body2">
                  {l10n.accountSettings.billing.expirationDate}&nbsp;
                  <Typography component="span" color="textPrimary" variant="inherit" className={classes.expirationDate}>
                    {billingData?.expiration.format('MM/YY')}
                    <CardStatusInlineAlert status={cardStatus} />
                  </Typography>
                </Typography>
              </Box>
            </ContentStack>
          </MobileOnly>
          <DesktopOnly>
            <Box display="flex" flexDirection="column" gap="0.5rem">
              <ContentStack value={cardEndingIn} />
              <Typography component="span" variant="body2">
                {l10n.accountSettings.billing.expirationDate}&nbsp;
                <Typography component="span" color="textPrimary" variant="body2" className={classes.expirationDate}>
                  {billingData?.expiration.format('MM/YY')}
                  <CardStatusInlineAlert status={cardStatus} />
                </Typography>
              </Typography>
            </Box>
          </DesktopOnly>
        </EditableSectionRow>
        <EditableSectionRow
          rowKey="billingAddress"
          label={l10n.accountSettings.billing.billingAddress}
          kind="custom"
          hideLabel={isMobile}
          hideRowDisplay={isBillingMissing}
          hideEditButton
        >
          <DesktopOnly>
            <ContentStack value={addressFormatted?.address}>
              <Box mt={1} />
              <Typography variant="body1">{addressFormatted?.addressCityState}</Typography>
            </ContentStack>
          </DesktopOnly>
          <MobileOnly>
            <ContentStack title={l10n.accountSettings.billing.billingAddress} value={addressFormatted?.fullAddress} />
          </MobileOnly>
        </EditableSectionRow>
      </Box>
      <Box p={2} display="flex" justifyContent="center" alignItems="center">
        <DesktopOnly>
          <Button
            onClick={onClick}
            variant="text"
            color="secondary"
          >
            {l10n.general.edit}
          </Button>
        </DesktopOnly>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  expirationDate: {
    fontWeight: 500,
    position: 'relative',
    '& > div': {
      paddingLeft: theme.spacing(1),
      paddingTop: 2,
      display: 'inline-flex',
      position: 'absolute',
      textWrap: 'nowrap',
    },
    '& svg': {
      transform: 'scale(0.7)',
    }
  },
  paddingBottom0: {
    paddingBottom: 0,
  },
}));
